import React from 'react';

import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import SanitizedHTML from 'components/ui/sanitized_html';

import ActionConfirmations from './action_confirmations';
import { AI_HTML_OPTIONS } from 'components/ai/html';
import { InlineTools, getClaimedActionIds } from '../tools/inline';
import { NextStepOffer, offeredStep } from '../tools/inline/next_step_offer';
import { StepCard, liveSteps } from '../tools/inline/step_card';
import { formatMetrics } from '../metrics/helpers';
import { useTranscriptContext } from './context';

import type { Components } from 'react-markdown';
import type { Message } from '../types';

// The .ai-assistant-prose styles already cover what markdown renders to; these only keep headings
// at the panel's size and send links to a new tab, matching AI_HTML_OPTIONS.
const markdownHeading: Components['h1'] = ({ children }) => <h4>{children}</h4>;
const markdownComponents: Components = {
  h1: markdownHeading,
  h2: markdownHeading,
  h3: markdownHeading,
  a: ({ href, children }) => <a href={href} rel="noopener noreferrer" target="_blank">{children}</a>,
};

interface Props {
  latest: boolean;
  markdown: boolean;
  message: Message;
  superseded: boolean;
}

export default function AssistantMessage({ latest, markdown, message, superseded }: Props) {
  const { busy, resolveAction, showAdminDetails } = useTranscriptContext();
  const runningSteps = liveSteps(message.steps, message.inlineToolCalls);
  const inlineToolCalls = message.inlineToolCalls || [];
  const claimed = getClaimedActionIds(inlineToolCalls);
  const actions = message.actions?.filter(action => !claimed.includes(action.id));
  const offer = latest && !message.isStreaming ? offeredStep(inlineToolCalls) : null;
  const proseClassName = classNames('ai-assistant-prose', { 'is-streaming': message.isStreaming && !message.status });

  return (
    <div className="mb-3">
      <div className={classNames({ 'text-danger': message.error })} style={{ maxWidth: '85%' }}>
        <InlineTools superseded={superseded} toolCalls={inlineToolCalls} />
        {runningSteps && <StepCard steps={runningSteps} />}
        {message.content && (markdown
          ? (
            // react-markdown does not render raw HTML, so this needs no sanitizing.
            <div className={proseClassName}>
              <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
            </div>
          )
          : <SanitizedHTML className={proseClassName} html={message.content} {...AI_HTML_OPTIONS} />
        )}
        {actions && actions.length > 0 && (
          <ActionConfirmations actions={actions} disabled={busy} onResolve={resolveAction} />
        )}
        {offer && <NextStepOffer question={offer} reply={message.content} />}
        {message.isStreaming && !runningSteps && (message.status || !message.content) && (
          <div className="small mt-1">
            <span className="ai-assistant-thinking">{message.status || 'Thinking'}…</span>
          </div>
        )}
        {showAdminDetails && message.metrics && (
          <div className="text-muted small mt-1">{formatMetrics(message.metrics)}</div>
        )}
      </div>
    </div>
  );
}
